/**
 * Configuración centralizada para tipos de actividad del sistema
 * Evita inconsistencias y facilita el mantenimiento
 */

import { getEmailDomainMark } from './config';

// Tipos principales de actividad
export const ACTIVITY_TYPES = {
  // Gestión de tareas
  CREATION: 'CREATION',
  EDIT: 'EDIT',
  TASK_DELETED: 'TASK_DELETED',
  TASK_DISCARDED: 'TASK_DISCARDED', // inProgress → todo
  TASK_RESTORED: 'TASK_RESTORED', // deleted/archived → inProgress
  
  // Estados
  STATUS_CHANGE: 'STATUS_CHANGE',
  
  // Asignaciones
  ASSIGNEE_CHANGE: 'ASSIGNEE_CHANGE',
  
  // Comentarios
  COMMENT_ADDED: 'COMMENT_ADDED',
  COMMENT: 'COMMENT', // Alias para compatibilidad
  COMMENT_EDITED: 'COMMENT_EDITED',
  EVIDENCE_COMMENT_ADDED: 'EVIDENCE_COMMENT_ADDED',
  COMMENT_DELETED: 'COMMENT_DELETED',
  
  // Archivos y enlaces
  ATTACHMENT: 'ATTACHMENT',
  URL_ADDED: 'URL_ADDED',
  
  // Fechas
  DEADLINE_CHANGE: 'DEADLINE_CHANGE'
};

// Campos específicos para el tipo EDIT
export const EDIT_FIELDS = {
  TITLE: 'title',
  DESCRIPTION: 'description',
  TAGS: 'tags',
  PRIORITY: 'priority',
  FILES: 'files',
  URL: 'url',
  END_DATE: 'endDate'
};

// Configuración visual y descriptiva para cada tipo de actividad
export const ACTIVITY_CONFIG = {
  [ACTIVITY_TYPES.CREATION]: {
    icon: '🆕',
    color: '#E8F5E9',
    getDescription: (userName, activity) => `${userName} creó la tarea "${activity.newValue}"`
  },
  
  [ACTIVITY_TYPES.EDIT]: {
    icon: '✏️',
    color: '#E3F2FD',
    getDescription: (userName, activity) => {
      switch (activity.field) {
        case EDIT_FIELDS.TITLE:
          return `${userName} cambió el título`;
        case EDIT_FIELDS.DESCRIPTION:
          return `${userName} actualizó la descripción`;
        case EDIT_FIELDS.TAGS:
          return `${userName} modificó las etiquetas`;
        case EDIT_FIELDS.PRIORITY:
          return `${userName} cambió la prioridad`;
        case EDIT_FIELDS.FILES:
          return `${userName} modificó los archivos adjuntos`;
        case EDIT_FIELDS.URL:
          return `${userName} actualizó los enlaces`;
        case EDIT_FIELDS.END_DATE:
          return `${userName} modificó la fecha de vencimiento`;
        default:
          return `${userName} modificó ${activity.field}`;
      }
    }
  },
  
  [ACTIVITY_TYPES.STATUS_CHANGE]: {
    icon: '🔄',
    color: '#FFF3E0',
    getDescription: (userName, activity) => {
      if (activity.newValue === 'done') {
        return `${userName} completó la tarea`;
      } else if (activity.newValue === 'inProgress' || activity.newValue === 'inprogress') {
        return `${userName} comenzó a trabajar en la tarea`;
      } else {
        return `${userName} cambió el estado de "${activity.oldValue}" a "${activity.newValue}"`;
      }
    }
  },
  
  [ACTIVITY_TYPES.TASK_DISCARDED]: {
    icon: '↩️',
    color: '#FFE0E0',
    getDescription: (userName, activity) => `${userName} descartó la tarea`
  },
  
  [ACTIVITY_TYPES.TASK_DELETED]: {
    icon: '🗑️',
    color: '#FFCDD2',
    getDescription: (userName, activity) => `${userName} eliminó la tarea`
  },

  [ACTIVITY_TYPES.TASK_RESTORED]: {
    icon: '♻️',
    color: '#E8F5E9',
    getDescription: (userName, activity) => `${userName} restauró la tarea a En progreso`
  },
  
  [ACTIVITY_TYPES.ASSIGNEE_CHANGE]: {
    icon: '👤',
    color: '#FFFDE7',
    getDescription: (userName, activity) => {
      if (!activity.oldValue && activity.newValue) {
        return `${userName} asignó la tarea a ${formatUserName(activity.newValue)}`;
      } else if (activity.oldValue && !activity.newValue) {
        return `${userName} quitó la asignación de la tarea`;
      } else if (activity.oldValue && activity.newValue) {
        return `${userName} cambió la asignación a ${formatUserName(activity.newValue)}`;
      }
      return `${userName} cambió la asignación`;
    }
  },
  
  [ACTIVITY_TYPES.COMMENT_ADDED]: {
    icon: '💬',
    color: '#F3E5F5',
    getDescription: (userName, activity) => `${userName} agregó un comentario`
  },
  
  [ACTIVITY_TYPES.COMMENT]: {
    icon: '💬',
    color: '#F3E5F5',
    getDescription: (userName, activity) => `${userName} agregó un comentario`
  },
  
  [ACTIVITY_TYPES.COMMENT_EDITED]: {
    icon: '✏️',
    color: '#F3E5F5',
    getDescription: (userName, activity) => `${userName} editó un comentario`
  },
  
  [ACTIVITY_TYPES.EVIDENCE_COMMENT_ADDED]: {
    icon: '📋',
    color: '#E8F5E9',
    getDescription: (userName, activity) => `${userName} agregó evidencia de finalización`
  },
  
  [ACTIVITY_TYPES.COMMENT_DELETED]: {
    icon: '🗑️',
    color: '#FFEBEE',
    getDescription: (userName, activity) => `${userName} eliminó un comentario`
  },
  
  [ACTIVITY_TYPES.ATTACHMENT]: {
    icon: '📎',
    color: '#E0F7FA',
    getDescription: (userName, activity) => `${userName} adjuntó archivos`
  },
  
  [ACTIVITY_TYPES.URL_ADDED]: {
    icon: '🔗',
    color: '#EDE7F6',
    getDescription: (userName, activity) => `${userName} agregó enlaces`
  },
  
  [ACTIVITY_TYPES.DEADLINE_CHANGE]: {
    icon: '📅',
    color: '#FFEBEE',
    getDescription: (userName, activity) => {
      if (!activity.oldValue) {
        return `${userName} estableció la fecha de vencimiento`;
      } else {
        return `${userName} cambió la fecha de vencimiento`;
      }
    }
  }
};

// Configuración por defecto para tipos no reconocidos
export const DEFAULT_ACTIVITY_CONFIG = {
  icon: '⚡',
  color: '#F5F5F5',
  getDescription: (userName, activity) => `${userName} realizó una acción en la tarea`
};

/**
 * Formatea el nombre de usuario desde email
 * @param {string} email - Email del usuario
 * @returns {string} - Nombre formateado
 */
export const formatUserName = (email) => {
  if (!email) return '';
  
  if (!email.includes('@')) return email;
  
  const namePart = email.split('@')[0];
  const nameWithSpaces = namePart.replace(/\./g, ' ');
  const nameParts = nameWithSpaces.split(' ');
  
  const firstName = nameParts[0] || '';
  const lastName = nameParts.length > 1 ? nameParts[1] : '';
  
  const formattedFirstName = firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase();
  const formattedLastName = lastName ? ' ' + lastName.charAt(0).toUpperCase() + lastName.slice(1).toLowerCase() : '';
  
  const mark = getEmailDomainMark(email);
  return formattedFirstName + formattedLastName + (mark ? ` ${mark}` : '');
};

/**
 * Obtiene la configuración de una actividad
 * @param {string} activityType - Tipo de actividad
 * @returns {object} - Configuración de la actividad
 */
export const getActivityConfig = (activityType) => {
  return ACTIVITY_CONFIG[activityType] || DEFAULT_ACTIVITY_CONFIG;
};

/**
 * Obtiene el icono de una actividad
 * @param {string} activityType - Tipo de actividad
 * @returns {string} - Icono de la actividad
 */
export const getActivityIcon = (activityType) => {
  return getActivityConfig(activityType).icon;
};

/**
 * Obtiene el color de una actividad
 * @param {string} activityType - Tipo de actividad
 * @returns {string} - Color de la actividad
 */
export const getActivityColor = (activityType) => {
  return getActivityConfig(activityType).color;
};

/**
 * Obtiene la descripción de una actividad
 * @param {string} activityType - Tipo de actividad
 * @param {object} activity - Datos de la actividad
 * @param {string} userEmail - Email del usuario (opcional, se toma de activity.updatedBy si no se proporciona)
 * @returns {string} - Descripción de la actividad
 */
export const getActivityDescription = (activityType, activity, userEmail = null) => {
  const config = getActivityConfig(activityType);
  const userName = formatUserName(userEmail || activity.updatedBy || activity.user || 'Usuario');
  return config.getDescription(userName, activity);
};

/**
 * Valida si un tipo de actividad es válido
 * @param {string} activityType - Tipo de actividad a validar
 * @returns {boolean} - True si es válido
 */
export const isValidActivityType = (activityType) => {
  return Object.values(ACTIVITY_TYPES).includes(activityType);
};

/**
 * Crea una entrada de actividad estandarizada
 * @param {string} type - Tipo de actividad
 * @param {string} userEmail - Email del usuario
 * @param {string} timestamp - Timestamp de la actividad
 * @param {object} options - Opciones adicionales (field, oldValue, newValue)
 * @returns {object} - Entrada de actividad
 */
export const createActivityEntry = (type, userEmail, timestamp, options = {}) => {
  if (!isValidActivityType(type)) {
    console.warn(`Tipo de actividad no válido: ${type}`);
  }
  
  return {
    timestamp,
    type,
    field: options.field || null,
    oldValue: options.oldValue || null,
    newValue: options.newValue || null,
    updatedBy: userEmail
  };
};